import { endOfYesterday, formatISO, isAfter, parse } from "date-fns";
import * as locales from "date-fns/locale";
import { CONSTANTS } from "../Configuration";
import { FormatDateOptions } from "react-intl";

/**
 * Calculate weekday number of a month.
 *
 * @param date
 * @returns Weekday number of a month.
 */
export function getWeekOfMonth(date?: Date): number {
  const newDate = date instanceof Date ? new Date(date) : new Date();
  let weekNumber = 1;

  /*
   * Try to calculate the first weekday (Requested Date weekday) of a month,
   * and count how many times we need to subtract 7(Total days of a week).
   * The number of subtractions will be the number of weekdays for the month.
   * For example - If Tuesday is on 25 July 2023, then the first Tuesday month will be on the 4th,
   * and we 4th times subtracted 7 to find this. Output - It is 4th Tuesday on 25 July 2023.
   */
  for (weekNumber; newDate.getDate() - 7 > 0; weekNumber++) {
    newDate.setDate(newDate.getDate() - 7);
  }

  // Last week of month will be a -1 for our system.
  return weekNumber === 5 ? -1 : weekNumber;
}

/**
 * Get current date week number of a current month.
 * @returns week day of current month. Example 1,2,3,4,-1
 */
export function getWeekNumberForCurrentMonth(): string {
  const weekNumber = getWeekOfMonth(getTodayDate());
  return weekNumber > 4 ? '-1' : `${weekNumber}`;
}

/**
 * Return today date.
 * @returns Today date
 */
export function getTodayDate(): Date {
  return new Date();
}

/**
 * Convert today date to time with 24 hour format. HH:mm
 * @returns Current time with 24 hour format.
 */
export function getCurrentTime(): string {
  const date = getTodayDate();
  return `${date.getHours()}:${date.getMinutes()}`;
}

/**
 * Convert today date to time with 24 hour format. HH:mm
 * @param date A valid date.
 * @returns Current time with 24 hour format.
 */
export function getTime(date: Date): string {
  return `${date.getHours()}:${date.getMinutes()}`;
}

/**
 * Determine the user time zone abbreviation. For example Asia/Calcutta to IST.
 * @returns Return a current time abbreviation.
 */
export function getCurrentTimeZoneAbbr(date: Date, formatDate: (value: string | number | Date | undefined, opts?: FormatDateOptions | undefined) => string): string {
  const dates = formatDate(date, { timeZoneName: "short" }).split(' ');
  return dates[dates.length - 1];
}

/**
 * Determine the user time zone. Exmaple Asia/Calcutta etc.
 * @returns timeZone string.
 */
export function getCurrentTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
  * Determine the user current weekday. Exmaple Monday, Tuesday etc.
  * @returns WeekDay string.
  */
export function getWeekDayFromDate(date: Date): string {
  return Intl.DateTimeFormat(undefined, { weekday: 'long', }).format(date);
}

/**
  * Determine the user current month. Exmaple March = 3, April =4 etc.
  * @returns month number.
  */
export function getCurrentMonth(): string {
  return Intl.DateTimeFormat(undefined, { month: 'numeric' }).format();
}

/**
  * Determine the user current day.
  * @param date - a date
  * @returns month number.
  */
export function getDateFromDate(date: Date): number {
  let dateLocal = date;
  if (!(dateLocal instanceof Date)) {
    dateLocal = new Date();
  }
  return dateLocal.getDate();
}

/**
  * Determine the date without time.
  * @returns month number.
  */
export function getDateWithoutTime(date: Date): string {
  return Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'numeric', year: 'numeric' }).format(date);
}

/**
 * Add month to a date.
 * @param date a date
 * @param month number of month to add.
 * @returns update date after adding month.
 */
export function addMonths(date: Date, month: number): Date {
  date.setMonth(date.getMonth() + month);
  return date;
}

/**
 * Get month from a date.
 * @param date a date
 * @returns update date after adding month.
 */
export function getMonth(date: Date): number {
  if (date instanceof Date) {
    return new Date(date).getMonth() + 1;
  } else {
    return new Date().getMonth() + 1
  }
}

/**
 * Get time zone abbreviation .
 * @param timeZone  - timeZone
 * @returns converted timeZone abbreviation.
 */
export function getTimeZoneAbbreviation(timeZone: string): string {
  return new Date().toLocaleString(undefined, { timeZone: timeZone, day: '2-digit', timeZoneName: 'short' }).slice(4);
}

/**
* Generate previous date of param date as min date.
* Example param data is 31 Dec 2029, then min date will be 30 Dec 2029
* @return {Date} 31 Dec, 2099
*/
export function getDefaultMinDate(date: Date): Date {
  let dateLocal = date;
  if (!(dateLocal instanceof Date)) {
    dateLocal = new Date();
  }
  return new Date(dateLocal.getTime() - 24 * 60 * 60 * 1000);
}

/*
* Generate next date of param date as max date.
* Example param data is 31 Dec 2029, then max date will be 1 Jan 2030
* @return {Date} 31 Dec, 2099
*/
export function getDefaultMaxDate(date: Date): Date {
  let dateLocal = date;
  if (!(dateLocal instanceof Date)) {
    dateLocal = new Date();
  }
  return new Date(dateLocal.getTime() + 24 * 60 * 60 * 1000);
}

/**
 * Returns the formatted date string.
 *
 * @param date - a valid date
 * @param formatDate - react-intl formate date function ref.
 * @param [hiddenTime=false] - true to hide the time fields including timezone.
 * @returns a date string
 */
export function getDate(date: string | null, formatDate: any, hiddenTime: boolean = false): string {
  if (!date)
    return "";

  return hiddenTime ? formatDate(date, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }) :
    formatDate(date, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: 'short'
    });
}

/**
 * Wrapper for formatISO from date-fns.
 *
 * @param date - a valid date.
 * @param options  - options.
 * @returns Formatted date string in ISO.
 */
export const formatDateToISO = (date: number | Date, options?: any): string => {
  return formatISO(date, options);
}

/**
 * Wrapper for isAfter from date-fns.
 *
 * @param date - a valid for compare.
 * @param dateToCompare - date to compare.
 * @returns Is date is after a date.
 */
export const isAfterDate = (date: number | Date, dateToCompare: number | Date): boolean => {
  return isAfter(date, dateToCompare);
}

/**
 * Wrapper for endOfYesterday from date-fns.
 * Get yesterday date.
 *
 * @returns yesterday date.
 */
export const getEndOfYesterday = (): Date => {
  return endOfYesterday();
}

/**
 * Wrapper for parse from date-fns.
 *
 * @param dateString - a valid date string
 * @param formatString - format string for a date
 * @param referenceDate - a reference date.
 * @param options - options
 * @returns Date
 */
export const parseStringToDate = (dateString: string, formatString: string, referenceDate: number | Date, options?: any): Date => {
  return parse(dateString, formatString, referenceDate, options);
}

/**
 * Get a date or time format pattern based on user current locale. 
 * If param isTimeFormat true then it will return the time format pattern string, otherwise date.
 * Example -  dd/MM/yyyy, MM/dd/yyyy etc.
 *
 * @param isTimeFormat - isTimeFormat true then it will return the time format pattern string, otherwise date.
 * @returns Date format pattern string
 */
export const getDateOrTimePattern = (isTimeFormat: boolean = false) => {
  const locale = getUserLocale();
  const rootLocale = locale.substring(0, 2);
  let localeObject = Object.values(locales).find(item => item.code === locale);
  localeObject = localeObject ? localeObject : Object.values(locales).find(item => item.code === rootLocale);
  const format = isTimeFormat ? localeObject?.formatLong?.time({ width: 'medium' }) : localeObject?.formatLong?.date({ width: 'medium' });
  return format ? format : CONSTANTS.dateFormatForUI;
}

/**
 * Get a user locale string.
 * Examples - hi-IN, eng-IN, eng, ar etc.
 *
 * @returns user locale string.
 */
export const getUserLocale = () => {
  return navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.language;
}

/**
 * Check the Time format is 24 hours or not.
 *
 * @returns true if time format is 24 hours.
 */
export const isTimePattern24Hours = () => {
  return getDateOrTimePattern(true)?.match(/H|HH|Ho|k|kk|ko/) ? true : false;
}
